import type { TableColumnsType } from 'antd'
import { Progress, Table } from 'antd'
import IconArrowDown from 'components/icons/IconArrowDown'
import IconArrowUp from 'components/icons/IconArrowUp'
import { EMetricType } from 'enums/common'
import { EKPICategory, EKPIUnit } from 'enums/kpi'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from 'utils/common'
import NumberUtil from 'utils/number'
import BaseExtraTable from '../BaseTable/BaseExtraTable'

interface DataType {
  key: React.Key
  name: string
  progress: number
  target: number
  baseline: number
  finalTabulated: number
  unit: EKPIUnit
}

interface IProps {
  data: any
  finalKpiScore?: number
  isStaff?: boolean
}

export const BaseMultipleChart: React.FC<IProps> = (props) => {
  const { data, finalKpiScore, isStaff } = props

  const { t } = useTranslation()

  const renderColor = (data: any) => {
    if (
      (data?.category === EKPICategory.REVERSE &&
        [EMetricType.BASELINE, EMetricType.STANDARD].includes(data?.metricType) &&
        data?.finalTabulated <= data?.baseline) ||
      (data?.category === EKPICategory.REVERSE &&
        data?.metricType === EMetricType.TARGET &&
        data?.finalTabulated <= data?.target) ||
      (data?.category !== EKPICategory.REVERSE &&
        [EMetricType.BASELINE, EMetricType.STANDARD].includes(data?.metricType) &&
        data?.finalTabulated >= data?.baseline) ||
      (data?.category !== EKPICategory.REVERSE &&
        data?.metricType === EMetricType.TARGET &&
        data?.finalTabulated >= data?.target)
    ) {
      return '#34B766'
    }
    return '#E43F1E'
  }

  const renderMultipleValueCell = (value: number, record: DataType): string => {
    return NumberUtil.format((value || 0).toFixed(2), { unit: record.unit as EKPIUnit })
  }

  const columns: TableColumnsType<DataType> = [
    {
      title: t('super_admin.dashboard.kpi'),
      dataIndex: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      sortIcon: ({ sortOrder }) => {
        return sortOrder === 'ascend' ? (
          <IconArrowUp fill='#14448C' />
        ) : sortOrder ? (
          <IconArrowDown fill='#14448C' />
        ) : (
          <IconArrowDown />
        )
      },
      width: '30%'
    },
    {
      title: t('super_admin.dashboard.progress'),
      dataIndex: 'progress',
      width: '30%',
      sorter: (a, b) => a.progress - b.progress,
      sortIcon: ({ sortOrder }) => {
        return sortOrder === 'ascend' ? (
          <IconArrowUp fill='#14448C' />
        ) : sortOrder ? (
          <IconArrowDown fill='#14448C' />
        ) : (
          <IconArrowDown />
        )
      },
      render: (progress, record) => (
        <Progress
          className='chart-progress'
          type='line'
          percentPosition={{ align: 'center', type: 'outer' }}
          percent={progress}
          size={['100%', 24]}
          strokeColor={renderColor(record)}
          format={() => (
            <span className={cn('text-neutral-medium text-sm font-medium')}>
              {Number(progress) ? progress.toFixed(0) + '%' : '0%'}
            </span>
          )}
        />
      )
    },
    {
      title: t('super_admin.dashboard.baseline'),
      dataIndex: 'baseline',
      render: renderMultipleValueCell
    },
    {
      title: t('super_admin.dashboard.target'),
      dataIndex: 'target',
      render: renderMultipleValueCell
    },
    {
      title: t('super_admin.dashboard.final_tabulated'),
      dataIndex: 'finalTabulated',
      render: renderMultipleValueCell
    }
  ]

  return (
    <>
      {finalKpiScore !== undefined ? (
        <BaseExtraTable
          className='overflow-y-auto multiple-chart custom-scroll h-full'
          columns={columns}
          headers={[
            {
              label: `${
                isStaff ? 'Final individual KPI score (out of 110)' : 'Final department KPI score (out of 110)'
              }`,
              description: 'KPI score is queried from the final quarter of the series when period is months',
              width: 318
            },
            {
              label: finalKpiScore.toFixed(2) + '%',
              width: 120
            }
          ]}
          data={data}
          loading={false}
        />
      ) : (
        <Table
          columns={columns}
          dataSource={data}
          pagination={false}
          className='overflow-y-auto multiple-chart h-full'
        />
      )}
    </>
  )
}
